<!-- 热门歌曲 -->
<script lang="ts" setup>
import { onMounted, onBeforeUnmount } from 'vue'
import type { ClipItem, ParsedClipItem } from '~/types/home'
import { getTrendingSongsList } from '~/apis/home'
import { decryptData } from '~/utils/crypto'
import HomePlay from '~/assets/icons/homePlay.svg?component'
import HomeUpvote from '~/assets/icons/homeUpvote.svg?component'
import HomeUnUpvote from '~/assets/icons/homeUnUpvote.svg?component'
import HomeShare from '~/assets/icons/homeShare.svg?component'
import HomeMore from '~/assets/icons/homeMore.svg?component'
import MoreActionGenerateSimilar from '~/assets/icons/moreActionGenerateSimilar.svg?component'
import HomeShuffle from '~/assets/icons/homeShuffle.svg?component'
import PlayingIcon from '~/assets/icons/playingIcon.gif'
import SongActionsMenu from '~/components/SongMoreActionsPopover.vue'
import { useRightSongsDetailStore } from '~/stores/rightSongsDetailStore'
import { usePlayerStore } from '~/stores/playerStore'
import { useToggleUpvote, useGenerateAction } from '~/composables/useCommonFunction'
import { useShareModalStore } from '~/stores/shareModalStore'

const { requireAuth } = useAuthGuard()
const rightSongsDetailStore = useRightSongsDetailStore()
const playerStore = usePlayerStore()
const shareModalStore = useShareModalStore()

// 点赞 / 取消点赞
const { handleToggleUpvote } = useToggleUpvote<ParsedClipItem>((uuid, updates) => {
  if (trendingSongs.value) {
    const index = trendingSongs.value.findIndex(s => s.uuid === uuid)
    if (index !== -1 && trendingSongs.value[index]) {
      // 创建新数组以确保响应式更新
      const updatedSongs = trendingSongs.value.map((item, i) =>
        i === index
          ? { ...item, isVote: updates.isVote, upvote_count: updates.upvote_count }
          : item
      )
      // 直接替换整个数组以触发响应式更新
      trendingSongs.value = updatedSongs
    }
  }
})

// 格式化数字显示
const formatNumber = (num: number): string => {
  if (num < 1000) {
    return num.toString()
  }
  return (num / 1000).toFixed(1) + 'K'
}

const openShare = (song: ParsedClipItem) => {
  const title = song.metadata?.type === 'upload' && song.title === 'my_2024-09-04_01_37_04' && song.fullPrompt?.title || song.title || song.metadata?.prompt || `Untitled - ${formatDate(song.createdAt)}`

  shareModalStore.openShare({
    title,
    uuid: song.uuid
  })
}

// generate similar
const { handleGenerateAction } = useGenerateAction<any>()
const handleGenerateSimilar = (song: ParsedClipItem) => handleGenerateAction(
  'generate_similar',
  { song, lockSongAction: true },
  { wrapSong: false }
)

// 处理播放按钮点击
const handlePlayClick = (song: ParsedClipItem) => {
  // 播放音乐，传递当前歌曲和播放列表
  if (trendingSongs.value && trendingSongs.value.length > 0) {
    playerStore.playSong(song, trendingSongs.value)
  } else {
    playerStore.playSong(song)
  }
  // 打开右侧详情页
  rightSongsDetailStore.openRightSongsDetail(song)
}

const handleEditSuccess = (song: ParsedClipItem, payload: { title: string, lyrics: string }) => {
  if (!trendingSongs.value) return
  const index = trendingSongs.value.findIndex(item => item.uuid === song.uuid)
  if (index === -1) return
  const target = trendingSongs.value[index]
  if (!target) return
  trendingSongs.value[index] = {
    ...target,
    title: payload.title?.trim() || target.title,
    fullPrompt: target.fullPrompt
      ? {
          ...target.fullPrompt,
          lyrics: payload.lyrics
        }
      : target.fullPrompt
  }
}

// 解析 ClipItem 中的 JSON 字符串字段
function parseClipItem(item: ClipItem): ParsedClipItem {
  let fullPrompt = null
  let fileInfo = null
  let metadata = null

  try {
    if (item.fullPrompt) {
      fullPrompt = JSON.parse(item.fullPrompt)
    }
  } catch (e) {
    console.warn('Failed to parse fullPrompt:', e)
  }

  try {
    if (item.fileInfo) {
      fileInfo = JSON.parse(item.fileInfo)
    }
  } catch (e) {
    console.warn('Failed to parse fileInfo:', e)
  }

  try {
    if (item.metadata) {
      metadata = JSON.parse(item.metadata)
    }
  } catch (e) {
    console.warn('Failed to parse metadata:', e)
  }

  return {
    ...item,
    fullPrompt,
    fileInfo,
    metadata
  }
}

// 格式化时长（秒转 mm:ss）
function formatDuration(seconds?: number): string {
  if (!seconds) return '00:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// 获取热门歌曲
const trendingPageSize = 6
const trendingCurrentPage = ref(1)
const trendingTotalCount = ref(0)

const { data: trendingSongs, refresh: refreshTrendingSongs } = await useAsyncData(
  'trending-songs',
  async () => {
    const res = await getTrendingSongsList({ page: trendingCurrentPage.value, size: trendingPageSize })
    const clips
      = typeof res.data?.clips === 'string'
        ? decryptData<ClipItem[]>(res.data.clips)
        : res.data?.clips

    if (typeof res.data?.count === 'number') {
      trendingTotalCount.value = res.data.count
    }

    console.log('Trending songs 的数据结构: %o', { clips: clips, count: res.data.count })

    return clips?.map(parseClipItem) || []
  }
)

// 全局点赞状态更新事件监听：当 uuid 匹配时，同步更新当前 Trending 列表里的 isVote 和 upvote_count
const handleUpvoteUpdated = (event: Event) => {
  const customEvent = event as CustomEvent<{ uuid?: string, isVote?: number, upvote_count?: number }>
  const detail = customEvent.detail
  if (!detail?.uuid || !trendingSongs.value || !Array.isArray(trendingSongs.value)) return

  const { uuid, isVote, upvote_count } = detail

  trendingSongs.value = trendingSongs.value.map((song) => {
    if (song.uuid !== uuid) return song
    return {
      ...song,
      isVote: typeof isVote === 'number' ? isVote : song.isVote,
      upvote_count: typeof upvote_count === 'number' ? upvote_count : song.upvote_count
    }
  })
}

onMounted(() => {
  if (typeof window === 'undefined') return
  window.addEventListener('song-upvote-updated', handleUpvoteUpdated as EventListener)
})

onBeforeUnmount(() => {
  if (typeof window === 'undefined') return
  window.removeEventListener('song-upvote-updated', handleUpvoteUpdated as EventListener)
})

// 随机刷新热门歌曲列表（随机页数，page 不超过 (总数 / 1) - 1）
const handleTrendingShuffleClick = () => {
  requireAuth(async () => {
    // 如果还没有总数，先刷新一次拿到 count
    if (!trendingTotalCount.value) {
      await refreshTrendingSongs()
    }

    if (!trendingTotalCount.value) return

    // 最大页数：不超过 (总数 / 1) - 1
    const maxPage = Math.max(0, Math.floor(trendingTotalCount.value / trendingPageSize) - 1)
    const randomPage = Math.floor(Math.random() * maxPage) + 1

    trendingCurrentPage.value = randomPage
    await refreshTrendingSongs()
  })
}

// 模型版本映射
const modelVersionMap = [
  { value: 'v5', label: 'v7' },
  { value: 'v4.5+', label: 'V6' },
  { value: 'v4.5', label: 'v5' },
  { value: 'v4', label: 'v4' }
]

// 根据 value 获取对应的 label
const getModelVersionLabel = (value: string | undefined | null): string => {
  if (!value) return ''
  const normalizedValue = value.toLowerCase()
  const mapped = modelVersionMap.find(item => item.value.toLowerCase() === normalizedValue)
  return mapped?.label || value
}

const formatDate = (value?: string | number | Date | null) => {
  if (!value) return ''

  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/)
    if (match && match[1]) return match[1]
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const y = date.getUTCFullYear()
  const m = `${date.getUTCMonth() + 1}`.padStart(2, '0')
  const d = `${date.getUTCDate()}`.padStart(2, '0')
  return `${y}-${m}-${d}`
}
</script>

<template>
  <div class="font-['Roboto'] px-[16px] mt-[16px] md:mt-[32px]">
    <!-- 顶部标题栏 -->
    <div class="flex justify-between items-center mb-[16px] md:mb-[20px]">
      <h2 class="text-white text-[16px] md:text-[24px] leading-[20px] md:leading-[28px] md:font-bold">
        Trending songs
      </h2>
      <button
        class="flex items-center text-[#8888A1] hover:text-[#E7E7EC] p-[4px] md:px-[8px] md:py-[6px] md:bg-[#262635] hover:bg-[#383847] rounded-[4px] cursor-pointer"
        @click="handleTrendingShuffleClick"
      >
        <HomeShuffle
          class="size-[16px]"
        />
        <span class="text-[14px] leading-[14px] ml-[6px] hidden md:inline-block">
          Shuffle
        </span>
      </button>
    </div>
    <!-- 卡片区域 -->
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-[16px] gap-y-[16px] md:gap-y-[20px]">
      <div
        v-for="song in trendingSongs"
        :key="song.uuid"
        v-memo="[
          song.uuid === playerStore.currentSong?.uuid,
          song.uuid === playerStore.currentSong?.uuid ? playerStore.isPlaying : null,
          song.isVote,
          song.upvote_count,
          song.play_count,
          song.title
        ]"
        :class="`flex bg-[#131318] hover:bg-[#262635] group ${playerStore.isPlaying && playerStore.currentSong?.uuid === song.uuid && 'bg-[#262635]'}`"
      >
        <!-- 左侧缩略图 -->
        <div class="relative flex-shrink-0 w-[38.26%] aspect-[1/1.376] bg-[#5E5E77] overflow-hidden">
          <img
            v-if="song.fileInfo?.cosUrl"
            :src="song.fileInfo.cosUrl"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
            :alt="song.title"
            :class="`w-full h-full object-cover scale-[1.1] transition-transform duration-200 group-hover:opacity-50 ${playerStore.isPlaying && playerStore.currentSong?.uuid === song.uuid && 'opacity-50'}`"
          >
          <!-- 模型型号和时长覆盖层 -->
          <div class="absolute top-[8px] right-[8px] md:top-[10px] md:right-[10px] flex items-center gap-x-[8px] p-[4px]">
            <span
              v-if="song?.metadata?.model_badges?.songrow?.display_name"
              class="text-[12px] text-white leading-[16px] bg-[#0A0A0A4D] px-[4px] rounded-[4px]"
            >
              {{ getModelVersionLabel(song?.metadata?.model_badges?.songrow?.display_name) }}
            </span>
            <span class="text-[12px] text-white leading-[16px] bg-[#0A0A0A4D] px-[4px] rounded-[4px]">
              {{ formatDuration(song.fileInfo?.duration) }}
            </span>
          </div>
          <!-- 播放中 -->
          <img
            v-if="playerStore.isPlaying && playerStore.currentSong?.uuid === song.uuid"
            class="absolute top-1/2 left-1/2 translate-[-50%] size-[28px] group-hover:opacity-0"
            :src="PlayingIcon"
          >
          <!-- 播放/暂停 -->
          <div
            class="absolute top-0 left-0 flex justify-center items-center size-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer"
            @click.stop="handlePlayClick(song)"
          >
            <UIcon
              v-if="!playerStore.isPlaying || playerStore.currentSong?.uuid !== song.uuid"
              name="weui:play-filled"
              class="size-[28px] text-[#E7E7EC]"
            />
            <UIcon
              v-else
              name="weui:pause-filled"
              class="size-[28px] text-[#E7E7EC]"
            />
          </div>
        </div>

        <!-- 右侧内容 -->
        <div class="flex flex-col justify-between flex-1 py-[16px] px-[8px] md:p-[8px]">
          <div>
            <!-- 标题 -->
            <NuxtLink :to="{ path: `/songDetail/${song.uuid}`, query: { from: 'home' } }">
              <h3 class="text-[14px] md:text-[16px] text-white md:font-bold leading-[20px] hover:underline truncate cursor-pointer">
                {{ song.metadata?.type === 'upload' && song.title === 'my_2024-09-04_01_37_04' && song.fullPrompt?.title || song.title || song.metadata?.prompt || `Untitled - ${formatDate(song.createdAt)}` }}
              </h3>
            </NuxtLink>

            <!-- 描述 -->
            <p class="text-[14px] text-[#85859F] leading-[20px] line-clamp-2 mt-[10px] md:hidden">
              {{ song.metadata?.tags || '(no styles)' }}
            </p>

            <!-- 互动指标 -->
            <div class="flex items-center justify-between mt-[10px] md:mt-[8px]">
              <div class="flex items-center gap-[4px]">
                <!-- 播放数 -->
                <div class="flex items-center text-[12px] text-[#8888A1] hover:text-[#E7E7EC] leading-[24px] hover:bg-[#383847] rounded-[4px] cursor-pointer">
                  <HomePlay
                    class="size-[16px] m-[4px]"
                  />
                  <span class="pr-[4px]">{{ formatNumber(song.play_count) }}</span>
                </div>

                <!-- 点赞数 -->
                <div
                  class="flex items-center text-[12px] text-[#8888A1] hover:text-[#E7E7EC] leading-[24px] hover:bg-[#383847] rounded-[4px] cursor-pointer"
                  @click="requireAuth(() => handleToggleUpvote(song))"
                >
                  <component
                    :is="song.isVote === 1 ? HomeUpvote : HomeUnUpvote"
                    :class="['size-[16px] m-[4px]', song.isVote === 1 ? 'text-[#F24B4B]' : 'text-[#8888A1]']"
                  />
                  <span class="pr-[4px]">{{ formatNumber(song.upvote_count) }}</span>
                </div>

                <!-- 分享按钮 -->
                <div
                  class="flex items-center text-[12px] text-[#8888A1] hover:text-[#E7E7EC] hover:bg-[#383847] rounded-[4px] cursor-pointer"
                  @click="requireAuth(() => openShare(song))"
                >
                  <HomeShare
                    class="size-[16px] m-[4px]"
                  />
                </div>
              </div>

              <!-- 更多选项 -->
              <div
                @click="requireAuth(() => {})"
              >
                <SongActionsMenu
                  :song="song"
                  :exclude-ids="['remix', 'download', 'edit_details', 'publish', 'move_to_trash']"
                  @edit-success="(payload) => handleEditSuccess(song, payload)"
                >
                  <div class="flex items-center text-[#B8B8C7] hover:text-[#E7E7EC] hover:bg-[#383847] rounded-[4px] ml-auto cursor-pointer">
                    <HomeMore class="size-[24px]" />
                  </div>
                </SongActionsMenu>
              </div>
            </div>

            <!-- 描述 -->
            <div class="hidden md:block">
              <p class="text-[14px] text-[#85859F] leading-[20px] line-clamp-2 mt-[10px]">
                {{ song.metadata?.tags || '(no styles)' }}
              </p>
            </div>
          </div>

          <!-- generate similar 按钮 -->
          <button
            class="flex items-center justify-center text-[12px] text-[#8888A1] hover:text-[#E7E7EC] hover:bg-[#383847] leading-[16px] border border-[#5E5E77] rounded-[8px] py-[8px] cursor-pointer"
            @click="requireAuth(() => handleGenerateSimilar(song))"
          >
            <MoreActionGenerateSimilar
              class="size-[16px]"
            />
            <span>generate similar</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
